import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, AlertTriangle, RotateCcw, Save, ExternalLink, FlaskConical } from 'lucide-react';
import {
  getDrawingAdminConfig,
  getDrawingModelsAvailable,
  getDrawingPrompts,
  patchDrawingAdminConfig,
  testDrawingFigureGen,
  type DrawingConfigResponse,
  type DrawingConfigWarning,
  type DrawingProvider,
  type DrawingPromptListItem,
  type DrawingTierConfig,
  type DrawingFigureGenTestResponse,
  type DrawingFigureGenTestTier,
} from '@/api/client';

interface Props {
  /** Logged-in user role — bounce non-admins. */
  userRole: string | undefined;
  /** Back to whichever view called the settings page (typically the
   * Drawing workbench). */
  onBack?: () => void;
  /** Open the existing prompts admin page. Wired by App.js (mirrors
   * the `onOpenPrompts` prop used by Home / TopBar). */
  onOpenPrompts?: () => void;
}

type EditableTierKey =
  | 'drawing_vision'
  | 'drawing_capable'
  | 'drawing_fast'
  | 'drawing_fallback'
  | 'drawing_figure_gen'
  | 'drawing_figure_gen_fallback';

// Narrow EditableTierKey down to the figure-gen subset for the
// FigureGenTestButton render guard. The button's tierKey prop only
// accepts the figure-gen pair; without a runtime narrowing TS can't
// prove that ``row.key`` is one of those when the loop iterates over
// all six EditableTierKey values, hence TS2322 at the JSX call site.
// Widening the button's prop union would let invalid keys through at
// runtime; this guard keeps the button's contract intact.
const FIGURE_GEN_TIER_KEYS = [
  'drawing_figure_gen',
  'drawing_figure_gen_fallback',
] as const;
type FigureGenTierKey = typeof FIGURE_GEN_TIER_KEYS[number];
function isFigureGenTierKey(key: string): key is FigureGenTierKey {
  return (FIGURE_GEN_TIER_KEYS as readonly string[]).includes(key);
}

interface TierRow {
  /** Synthetic key — drives the row's identity. The figure_gen fallback
   * row maps to the same `drawing_figure_gen` PATCH but populates the
   * `fallback_provider` / `fallback_model` fields instead. */
  key: EditableTierKey;
  label: string;
  helpText: string;
  defaultProvider: string;
  defaultModel: string;
  group: 'models' | 'figure_gen';
}

/** Defaults match the values seeded by `seeds/system_config.py`. The
 * "Reset to defaults" button only resets in-memory state; the user
 * still has to click Save to persist (per brief). */
const TIER_ROWS: TierRow[] = [
  {
    key: 'drawing_vision',
    label: 'Vision tier',
    helpText: 'Used for figure caption, claim-prime, analyze-bundle prompts.',
    defaultProvider: 'anthropic',
    defaultModel: 'claude-sonnet-4-6',
    group: 'models',
  },
  {
    key: 'drawing_capable',
    label: 'Capable tier',
    helpText: 'Used for auto-map terms and claim-edit impact prompts.',
    defaultProvider: 'anthropic',
    defaultModel: 'claude-sonnet-4-6',
    group: 'models',
  },
  {
    key: 'drawing_fast',
    label: 'Fast tier',
    helpText: 'Used for cheap classification calls (numeral assignment, etc.).',
    defaultProvider: 'anthropic',
    defaultModel: 'claude-haiku-4-5',
    group: 'models',
  },
  {
    key: 'drawing_fallback',
    label: 'Fallback',
    helpText: 'Used when the primary fast-tier call fails.',
    defaultProvider: 'anthropic',
    defaultModel: 'claude-haiku-4-5',
    group: 'models',
  },
  {
    key: 'drawing_figure_gen',
    label: 'Primary',
    helpText: 'Used for AI figure generation (Phase 7).',
    defaultProvider: 'fal',
    defaultModel: 'nano-banana',
    group: 'figure_gen',
  },
  {
    key: 'drawing_figure_gen_fallback',
    label: 'Fallback',
    helpText: 'Used when the primary figure-gen call fails.',
    defaultProvider: 'openai',
    defaultModel: 'sora-image',
    group: 'figure_gen',
  },
];

interface PendingValue { provider: string; model: string; }

const inputClass =
  'flex h-9 rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:opacity-50';

/** BUG-023 — Axios surfaces FastAPI's HTTPException ``detail`` as
 * either a plain string OR a structured dict (e.g. the image-gen
 * model guard returns ``{error_code, detail, suggestions}``).
 * Setting the dict directly into a render state produced
 * ``[object Object]`` in the result banner. This helper picks the
 * best string out of any of those shapes and falls back to
 * ``e.message`` then a sane default. */
function _extractErrorMessage(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (detail && typeof detail === 'object') {
    const d = detail as Record<string, unknown>;
    if (typeof d.detail === 'string' && d.detail) return d.detail;
    if (typeof d.message === 'string' && d.message) return d.message;
    if (typeof d.error_code === 'string' && d.error_code) {
      return `${d.error_code}`;
    }
  }
  const msg = (e as { message?: string })?.message;
  if (typeof msg === 'string' && msg) return msg;
  return fallback;
}

const primaryBtn =
  'inline-flex h-9 items-center justify-center gap-2 rounded-sm bg-[#002FA7] px-4 ' +
  'text-sm font-medium text-white hover:bg-[#002277] disabled:opacity-50 transition-colors';

const outlineBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 ' +
  'transition-colors';

/**
 * Drawing-isolated model settings page (admin-only).
 *
 * The brief specifies `/matter/:matterId/drawings/settings` but this
 * codebase uses Zustand `view` state for navigation. App.js routes
 * `view === 'drawings-settings'` here and bounces non-admins back.
 *
 * Backed by Phase 3B's three admin endpoints:
 *   - GET   /admin/drawings/config           (populate forms)
 *   - GET   /admin/drawings/models/available (dropdown options)
 *   - GET   /admin/drawings/prompts          (drawing prompts list)
 *
 * Save fires one PATCH per changed tier; figure_gen primary + fallback
 * are bundled into a single PATCH (the backend accepts optional
 * fallback_provider / fallback_model fields on `drawing_figure_gen`).
 */
export default function DrawingSettings({ userRole, onBack, onOpenPrompts }: Props) {
  const isAdmin = userRole === 'admin';

  const [config, setConfig] = useState<DrawingConfigResponse | null>(null);
  const [providers, setProviders] = useState<DrawingProvider[]>([]);
  const [prompts, setPrompts] = useState<DrawingPromptListItem[]>([]);
  const [pending, setPending] = useState<Record<EditableTierKey, PendingValue> | null>(null);
  const [loadingState, setLoadingState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  // Post-walk finding 4 — image-gen guard returned `None` on the
  // last save (typically because the figure_gen tier points at
  // openrouter / local / custom / nvidia / an uncatalogued
  // openai|google route). Surface the backend-emitted warnings as
  // amber banners; cleared on every fresh Save / Reset.
  const [saveWarnings, setSaveWarnings] = useState<DrawingConfigWarning[]>([]);

  // ── Fetch config + models + prompts on mount (admins only) ─────
  const loadAll = useCallback(async () => {
    setLoadingState('loading');
    setErrorMsg(null);
    try {
      const [cfg, models, promptsList] = await Promise.all([
        getDrawingAdminConfig(),
        getDrawingModelsAvailable(),
        getDrawingPrompts(),
      ]);
      setConfig(cfg);
      setProviders(models.providers || []);
      setPrompts(promptsList.prompts || []);
      setPending(buildPendingFromConfig(cfg));
      setLoadingState('ready');
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail
        || (e as { message?: string })?.message
        || 'Failed to load drawing settings.';
      setErrorMsg(message);
      setLoadingState('error');
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadAll();
  }, [isAdmin, loadAll]);

  // ── Computed: which tiers have pending changes vs. config ──────
  const dirtyKeys = useMemo<Set<EditableTierKey>>(() => {
    const dirty = new Set<EditableTierKey>();
    if (!pending || !config) return dirty;
    for (const row of TIER_ROWS) {
      const seedFromConfig = readEffective(config, row.key);
      if (
        pending[row.key].provider !== seedFromConfig.provider
        || pending[row.key].model !== seedFromConfig.model
      ) {
        dirty.add(row.key);
      }
    }
    return dirty;
  }, [pending, config]);

  const hasUnsaved = dirtyKeys.size > 0;

  // ── Save: one PATCH per changed tier; figure_gen + its fallback
  // are merged into a single PATCH so the backend writes both
  // fallback_provider/_model atomically. ────────────────────────
  const handleSave = useCallback(async () => {
    if (!pending) return;
    setSaving(true);
    setSaveResult(null);
    setSaveWarnings([]);
    const errors: string[] = [];
    const accumulatedWarnings: DrawingConfigWarning[] = [];

    const changed = TIER_ROWS.filter((r) => dirtyKeys.has(r.key));
    const figureGenChanged = changed.some((r) =>
      r.key === 'drawing_figure_gen' || r.key === 'drawing_figure_gen_fallback',
    );

    try {
      // Non figure_gen tiers — one PATCH each.
      for (const r of changed) {
        if (r.key === 'drawing_figure_gen' || r.key === 'drawing_figure_gen_fallback') continue;
        const r1 = await patchDrawingAdminConfig({
          tier: r.key,
          provider: pending[r.key].provider,
          model: pending[r.key].model,
        });
        if (r1.warnings) accumulatedWarnings.push(...r1.warnings);
      }
      // figure_gen primary + fallback in one PATCH.
      if (figureGenChanged) {
        const r2 = await patchDrawingAdminConfig({
          tier: 'drawing_figure_gen',
          provider: pending['drawing_figure_gen'].provider,
          model: pending['drawing_figure_gen'].model,
          fallback_provider: pending['drawing_figure_gen_fallback'].provider,
          fallback_model: pending['drawing_figure_gen_fallback'].model,
        });
        if (r2.warnings) accumulatedWarnings.push(...r2.warnings);
      }
    } catch (e: unknown) {
      errors.push(_extractErrorMessage(e, 'PATCH failed.'));
    }

    setSaving(false);
    if (errors.length === 0) {
      setSaveResult({ kind: 'success', message: 'Saved.' });
      setSaveWarnings(accumulatedWarnings);
      // Re-fetch to surface whatever the backend persisted (handles
      // server-side normalisation if any).
      await loadAll();
    } else {
      setSaveResult({ kind: 'error', message: errors.join(' ') });
    }
  }, [pending, dirtyKeys, loadAll]);

  // ── Reset: confirm → PATCH every tier back to its seed default
  // → reload. BUG-023 — the prior version only flipped local state
  // and surfaced a 'click Save to persist' nudge; admins reported
  // confusion when a stale Save error left an [object Object]
  // banner up after a Reset, because the save-error path used
  // `?.response?.data?.detail` directly without string-coercing
  // the structured-error dict the backend ships for image-gen
  // model rejects. Persistence now happens inside Reset itself,
  // and the helper string-coerces every error-detail value.
  const handleReset = useCallback(async () => {
    if (!pending) return;
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const ok = window.confirm(
        'Reset all drawing model settings to defaults? This cannot be undone.',
      );
      if (!ok) return;
    }

    setSaving(true);
    setSaveResult(null);
    setSaveWarnings([]);
    const errors: string[] = [];
    const accumulatedWarnings: DrawingConfigWarning[] = [];
    try {
      // Text tiers — one PATCH each.
      for (const r of TIER_ROWS) {
        if (r.key === 'drawing_figure_gen' || r.key === 'drawing_figure_gen_fallback') continue;
        const r1 = await patchDrawingAdminConfig({
          tier: r.key,
          provider: r.defaultProvider,
          model: r.defaultModel,
        });
        if (r1.warnings) accumulatedWarnings.push(...r1.warnings);
      }
      // Figure-gen primary + fallback in a single PATCH (mirrors
      // the save path's contract).
      const fgPrimary = TIER_ROWS.find((r) => r.key === 'drawing_figure_gen')!;
      const fgFallback = TIER_ROWS.find((r) => r.key === 'drawing_figure_gen_fallback')!;
      const r2 = await patchDrawingAdminConfig({
        tier: 'drawing_figure_gen',
        provider: fgPrimary.defaultProvider,
        model: fgPrimary.defaultModel,
        fallback_provider: fgFallback.defaultProvider,
        fallback_model: fgFallback.defaultModel,
      });
      if (r2.warnings) accumulatedWarnings.push(...r2.warnings);
    } catch (e: unknown) {
      errors.push(_extractErrorMessage(e, 'Reset PATCH failed.'));
    }

    setSaving(false);
    if (errors.length === 0) {
      setSaveResult({ kind: 'success', message: 'Reset to defaults.' });
      setSaveWarnings(accumulatedWarnings);
      // Re-fetch so the form shows what the backend persisted.
      await loadAll();
    } else {
      setSaveResult({ kind: 'error', message: errors.join(' ') });
    }
  }, [pending, loadAll]);

  // ── Render ────────────────────────────────────────────────────

  if (!isAdmin) {
    // Bounce non-admins. Render a minimal 403 panel and call onBack
    // after a tick so the back-history is consistent.
    return (
      <div className="min-h-screen bg-zinc-50">
        <Header onBack={onBack} />
        <div className="mx-auto max-w-md px-6 py-16 text-center" data-testid="drawings-settings-forbidden">
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-amber-500" aria-hidden />
          <h2 className="mb-2 text-base font-semibold text-zinc-900">
            Admins only
          </h2>
          <p className="text-sm text-zinc-600">
            Drawing settings are admin-only. Ask your workspace admin
            to make changes here.
          </p>
        </div>
      </div>
    );
  }

  if (loadingState === 'loading' || loadingState === 'idle') {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Header onBack={onBack} />
        <div
          className="flex min-h-[calc(100vh-3.5rem)] items-center justify-center text-sm text-zinc-500"
          data-testid="drawings-settings-loading"
        >
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          Loading drawing settings…
        </div>
      </div>
    );
  }

  if (loadingState === 'error') {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Header onBack={onBack} />
        <div className="mx-auto max-w-xl px-6 py-16 text-center" data-testid="drawings-settings-error">
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-amber-500" aria-hidden />
          <h2 className="mb-2 text-base font-semibold text-zinc-900">
            Could not load drawing settings
          </h2>
          <p className="mb-4 text-sm text-zinc-600">{errorMsg}</p>
          <button type="button" className={outlineBtn} onClick={loadAll}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header onBack={onBack} />
      <div className="mx-auto max-w-3xl px-6 py-8" data-testid="drawings-settings">
        <h1 className="mb-1 text-xl font-semibold text-zinc-900">
          Drawing module settings
        </h1>
        <p className="mb-6 text-sm text-zinc-600">
          These models drive drawing-module LLM calls only. Claims-side
          models in <strong>Settings → Models</strong> are independent.
        </p>

        <Section title="Model selection">
          {TIER_ROWS.filter((r) => r.group === 'models').map((row) => (
            <TierEditor
              key={row.key}
              row={row}
              providers={providers}
              value={pending![row.key]}
              dirty={dirtyKeys.has(row.key)}
              onChange={(provider, model) =>
                setPending((p) => p && { ...p, [row.key]: { provider, model } })}
            />
          ))}
        </Section>

        <Section title="Figure generation">
          {TIER_ROWS.filter((r) => r.group === 'figure_gen').map((row) => (
            <TierEditor
              key={row.key}
              row={row}
              providers={providers}
              value={pending![row.key]}
              dirty={dirtyKeys.has(row.key)}
              onChange={(provider, model) =>
                setPending((p) => p && { ...p, [row.key]: { provider, model } })}
            />
          ))}
        </Section>

        <div className="my-6 flex items-center gap-3">
          <button
            type="button"
            className={primaryBtn}
            disabled={!hasUnsaved || saving}
            onClick={handleSave}
            data-testid="drawings-settings-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save changes
          </button>
          <button
            type="button"
            className={outlineBtn}
            onClick={handleReset}
            disabled={saving}
            data-testid="drawings-settings-reset"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to defaults
          </button>
          {saveResult && (
            <span
              className={[
                'text-xs font-medium',
                saveResult.kind === 'success' ? 'text-green-700' : 'text-red-700',
              ].join(' ')}
              role="status"
              data-testid="drawings-settings-result"
            >
              {saveResult.message}
            </span>
          )}
        </div>
        {/* Post-walk finding 4 — image-gen guard returned None on
            this save (e.g. openrouter / local / custom / nvidia,
            or an uncatalogued openai|google route). Render an
            amber banner per warning so the admin sees the
            uncertainty without it being a blocking error. */}
        {saveWarnings.length > 0 && (
          <div
            className="mb-4 space-y-1.5 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900"
            role="alert"
            data-testid="drawings-settings-warnings"
          >
            {saveWarnings.map((w, i) => (
              <div key={`${w.code}-${w.side ?? 'x'}-${i}`}>
                <span className="font-mono text-[10px] uppercase tracking-widest text-amber-800">
                  {w.code}
                  {w.side ? ` · ${w.side}` : ''}
                </span>
                <p className="mt-0.5">{w.message}</p>
              </div>
            ))}
          </div>
        )}

        <Section title="Drawing prompts">
          <p className="mb-3 text-sm text-zinc-600">
            Drawing prompts are edited in the main Prompts admin. The
            drawing subset is filtered by <code className="rounded bg-zinc-100 px-1">stage='drawing'</code>{' '}
            ({prompts.length} prompt{prompts.length === 1 ? '' : 's'} active).
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={outlineBtn}
              onClick={onOpenPrompts}
              disabled={!onOpenPrompts}
              data-testid="drawings-settings-open-prompts"
            >
              <ExternalLink className="h-4 w-4" />
              Open Prompts admin
            </button>
            <button
              type="button"
              className={`${outlineBtn} disabled:cursor-not-allowed`}
              disabled
              title="Test-fire flow re-uses the existing /admin/prompts test modal — wires up in slice 4G."
              data-testid="drawings-settings-test-prompt"
            >
              <FlaskConical className="h-4 w-4" />
              Test drawing prompt
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────

function Header({ onBack }: { onBack?: () => void }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-3">
      <div className="flex items-center gap-3">
        {onBack && (
          <button type="button" className={outlineBtn} onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        )}
        <h1 className="text-lg font-semibold text-zinc-900">Drawing module settings</h1>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6 rounded-md border border-zinc-200 bg-white">
      <header className="border-b border-zinc-200 bg-zinc-50 px-4 py-2.5">
        <h2 className="text-sm font-semibold text-zinc-700">{title}</h2>
      </header>
      <div className="space-y-4 px-4 py-4">{children}</div>
    </section>
  );
}

interface TierEditorProps {
  row: TierRow;
  providers: DrawingProvider[];
  value: PendingValue;
  dirty: boolean;
  onChange: (provider: string, model: string) => void;
}

function TierEditor({ row, providers, value, dirty, onChange }: TierEditorProps) {
  const providerOptions = providers.map((p) => p.id);
  const modelsForProvider = useMemo(
    () => (providers.find((p) => p.id === value.provider)?.models || []).map((m) => m.id),
    [providers, value.provider],
  );
  // Model ids are typed, never picked from a fixed list: providers ship
  // new models faster than any catalogue here can follow (switching
  // figure generation to Google offered only text models). The
  // provider's known ids are suggestions in a datalist; anything typed
  // is saved as is.
  const isImageTier = row.group === 'figure_gen';
  const suggestions = isImageTier
    ? modelsForProvider.filter((m) => {
      const hint = (providers.find((p) => p.id === value.provider)?.models || [])
        .find((x) => x.id === m)?.tier_hint;
      return hint === 'figure_gen' || /image|imagen|dall-e|flux|seedream|banana/i.test(m);
    })
    : modelsForProvider;
  const listId = `tier-model-options-${row.key}`;
  const imageHint: Record<string, string> = {
    google: 'An image model from Google AI Studio, e.g. gemini-2.5-flash-image or imagen-4.0-generate-001.',
    openrouter: 'An image generation model on OpenRouter.',
    openai: 'An OpenAI image model, e.g. gpt-image-1.',
  };

  return (
    <div className="grid grid-cols-[160px_1fr] items-start gap-3" data-testid={`tier-row-${row.key}`}>
      <div className="pt-1.5">
        <div className="flex items-center gap-2 text-sm font-medium text-zinc-800">
          {row.label}
          {dirty && (
            <span
              className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200"
              title="Unsaved change"
            >
              unsaved
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-zinc-500">{row.helpText}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col text-[11px] font-medium text-zinc-500">
          Provider
          <select
            className={`${inputClass} mt-1`}
            value={value.provider}
            onChange={(e) => onChange(e.target.value, '')}
            data-testid={`tier-provider-${row.key}`}
          >
            <option value="">—</option>
            {providerOptions.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-[11px] font-medium text-zinc-500">
          Model
          <input
            type="text"
            list={listId}
            className={`${inputClass} mt-1`}
            value={value.model}
            onChange={(e) => onChange(value.provider, e.target.value.trim())}
            placeholder={suggestions[0] || row.defaultModel}
            disabled={!value.provider}
            spellCheck={false}
            autoComplete="off"
            data-testid={`tier-model-${row.key}`}
          />
          <datalist id={listId}>
            {suggestions.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          <span className="mt-1 text-[10px] text-zinc-400">
            {isImageTier
              ? (imageHint[value.provider] || 'Must be an image generation model.')
                + ' Type any model id; the list only suggests.'
              : 'Type any model id from your provider\'s documentation; the list only suggests.'}
          </span>
        </label>
      </div>
      {row.group === 'figure_gen' && isFigureGenTierKey(row.key) && (
        <div className="col-span-2">
          <FigureGenTestButton tierKey={row.key} />
        </div>
      )}
    </div>
  );
}

// ─── Figure-gen Test button (BUG-016 part D) ─────────────────────
//
// Fires POST /admin/drawings/figure-gen/test against the saved
// provider/model for the row's tier and renders either a thumbnail
// preview on success or a red banner on failure. Saves first via
// the existing PATCH path are the admin's responsibility — the
// helper text below makes that explicit.
function FigureGenTestButton({
  tierKey,
}: {
  tierKey: 'drawing_figure_gen' | 'drawing_figure_gen_fallback';
}) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DrawingFigureGenTestResponse | null>(null);

  const apiTier: DrawingFigureGenTestTier =
    tierKey === 'drawing_figure_gen_fallback'
      ? 'drawing_figure_gen_fallback'
      : 'drawing_figure_gen';

  const onTest = useCallback(async () => {
    setRunning(true);
    setResult(null);
    try {
      const resp = await testDrawingFigureGen(apiTier);
      setResult(resp);
    } catch (e: unknown) {
      // 4xx / network errors land here — surface as the same shape
      // so the render branch below stays uniform.
      const detail =
        (e as { response?: { data?: { detail?: { detail?: string } } }; message?: string })
          ?.response?.data?.detail?.detail
        ?? (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? (e as { message?: string })?.message
        ?? 'Network error.';
      setResult({
        ok: false,
        error_code: 'request_failed',
        detail: typeof detail === 'string' ? detail : 'Network error.',
      });
    } finally {
      setRunning(false);
    }
  }, [apiTier]);

  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onTest}
          disabled={running}
          className="inline-flex h-7 items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          data-testid={`figure-gen-test-${tierKey}`}
        >
          {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <FlaskConical className="h-3 w-3" />}
          {running ? 'Generating…' : 'Test image generation'}
        </button>
        <span className="text-[10px] text-zinc-400">
          Save your changes first — the test fires against the saved provider/model.
        </span>
      </div>
      {result && result.ok && (
        <div
          className="flex items-start gap-3 rounded-sm border border-green-200 bg-green-50 p-2"
          data-testid={`figure-gen-test-success-${tierKey}`}
        >
          <img
            src={`data:${result.mime};base64,${result.image_b64}`}
            alt="Test generation result"
            className="h-24 w-24 flex-shrink-0 rounded-sm border border-zinc-200 bg-white object-contain"
          />
          <div className="min-w-0 flex-1 text-[11px] text-green-900">
            <p className="font-medium">✓ Image generated successfully</p>
            <p className="mt-0.5 font-mono text-zinc-600">
              {result.provider} · {result.model} · {result.duration_ms}ms
            </p>
          </div>
        </div>
      )}
      {result && !result.ok && (() => {
        // Hoist the narrowed branch into a typed local so the JSX
        // expression below sees the failure-shape fields (detail /
        // error_code / optional provider). The implicit narrowing
        // through ``result && !result.ok &&`` doesn't propagate
        // past the IIFE call boundary in TS' control-flow analysis
        // for JSX expressions in this codebase, so the explicit
        // Extract<> annotation makes it explicit.
        const failure = result as Extract<DrawingFigureGenTestResponse, { ok: false }>;
        return (
          <div
            className="rounded-sm border border-red-200 bg-red-50 p-2 text-[11px] text-red-800"
            data-testid={`figure-gen-test-error-${tierKey}`}
          >
            <p className="font-medium">✗ Failed: {failure.detail}</p>
            <p className="mt-0.5 font-mono text-red-700/80">
              error_code={failure.error_code}
              {failure.provider ? ` · provider=${failure.provider}` : ''}
            </p>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────

function readEffective(config: DrawingConfigResponse, key: EditableTierKey): PendingValue {
  if (key === 'drawing_figure_gen_fallback') {
    const fg = config.tiers.drawing_figure_gen as DrawingTierConfig | undefined;
    return {
      provider: fg?.fallback_provider || '',
      model: fg?.fallback_model || '',
    };
  }
  const t = config.tiers[key];
  return { provider: t?.provider || '', model: t?.model || '' };
}

function buildPendingFromConfig(
  config: DrawingConfigResponse,
): Record<EditableTierKey, PendingValue> {
  const next = {} as Record<EditableTierKey, PendingValue>;
  for (const r of TIER_ROWS) {
    next[r.key] = readEffective(config, r.key);
  }
  return next;
}
